import { Link } from "react-router-dom";
import { Heart, Bath, BedDouble, Ruler, BadgeCheck, Crown, Scale, MapPin, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatPrice } from "@/data/properties";
import "@/pages/map-search/mapsearch.css";

const FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80";

// Deterministic 90-98% "AI Match" score derived from the property id so it is
// stable across renders (marketing display, matches FeaturedHeroCard).
const aiMatchScore = (id = "") => {
  let h = 0;
  for (let i = 0; i < String(id).length; i += 1) h = (h * 31 + String(id).charCodeAt(i)) % 1000;
  return 90 + (h % 9); // 90..98
};

export const PropertyCardPremium = ({
  property, active = false, favorite = false, compared = false,
  onHover, onLeave, onFavorite, onCompare,
}) => {
  const p = property || {};
  const cover = (p.images || []).find((i) => i.is_cover) || (p.images || [])[0];
  const img = cover?.url || FALLBACK;
  const dealLabel = p.deal === "rent" ? "/mo" : "";

  return (
    <article
      data-testid="property-result-card"
      onMouseEnter={() => onHover?.(p.id)}
      onMouseLeave={() => onLeave?.()}
      className={`group overflow-hidden rounded-[var(--ms-radius-lg)] border bg-[hsl(var(--ms-card))] shadow-[var(--ms-shadow-md)] transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[var(--ms-shadow-lg)] ${active ? "ring-2 ring-[hsl(var(--ms-blue))]" : "border-[hsl(var(--ms-border))]"}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <Link to={`/properties/${p.id}`}>
          <img src={img} alt={p.title} loading="lazy" decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]" />
        </Link>
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {p.verified && (
            <Badge data-testid="property-card-verified-badge" className="rounded-full bg-[#10B981] text-white hover:bg-[#10B981]"><BadgeCheck className="mr-1 h-3.5 w-3.5" /> Verified</Badge>
          )}
          {p.featured && (
            <Badge data-testid="property-card-premium-badge" className="rounded-full bg-[#1E3A8A] text-white hover:bg-[#1E3A8A]"><Crown className="mr-1 h-3.5 w-3.5" /> Premium</Badge>
          )}
        </div>
        <div className="absolute right-3 top-3 flex gap-1.5">
          <button type="button" onClick={() => onFavorite?.(p)} data-testid="property-card-favorite-button" aria-label="Save to favorites"
            className="ms-glass flex h-11 w-11 items-center justify-center rounded-full text-slate-700 transition-transform duration-150 hover:scale-105 lg:h-9 lg:w-9">
            <Heart className={`h-4 w-4 ${favorite ? "fill-[#DC2626] text-[#DC2626]" : ""}`} />
          </button>
          <button type="button" onClick={() => onCompare?.(p)} data-testid="property-card-compare-button" aria-label="Add to compare"
            className={`ms-glass flex h-11 w-11 items-center justify-center rounded-full transition-transform duration-150 hover:scale-105 lg:h-9 lg:w-9 ${compared ? "text-[#1E3A8A]" : "text-slate-700"}`}>
            <Scale className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-lg font-extrabold tabular-nums tracking-tight text-[hsl(var(--ms-blue))]" data-testid="property-card-price">
            {formatPrice(Number(p.price), p.currency)}<span className="text-xs font-medium text-[hsl(var(--ms-muted-fg))]">{dealLabel}</span>
          </p>
        </div>
        <Link to={`/properties/${p.id}`} className="mt-1 line-clamp-1 text-base font-semibold leading-snug text-[hsl(var(--ms-fg))] hover:text-[hsl(var(--ms-blue))]">{p.title}</Link>
        <p className="mt-1 flex items-center gap-1 text-xs text-[hsl(var(--ms-muted-fg))]"><MapPin className="h-3.5 w-3.5" /> <span className="line-clamp-1">{p.address || "Ethiopia"}</span></p>
        <span data-testid="property-card-ai-match" className="mt-2 inline-flex w-fit items-center gap-1 rounded-full bg-[#10B981]/12 px-2.5 py-0.5 text-[11px] font-semibold text-[#0E9F6E] dark:text-[#34D399]">
          <Star className="h-3 w-3 fill-current" /> AI Match {aiMatchScore(p.id)}%
        </span>
        <div className="mt-3 flex items-center gap-3 text-xs text-[hsl(var(--ms-muted-fg))]">
          <span className="flex items-center gap-1"><BedDouble className="h-4 w-4" /> {p.bedrooms ?? 0}</span>
          <span className="flex items-center gap-1"><Bath className="h-4 w-4" /> {p.bathrooms ?? 0}</span>
          {p.square_meters ? <span className="flex items-center gap-1"><Ruler className="h-4 w-4" /> {p.square_meters} m²</span> : null}
        </div>
      </div>
    </article>
  );
};

export default PropertyCardPremium;
